import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import { labelKeys } from "../../../../../api/queryKeys";
import CustomToolTip from "../../../../../components/CustomTooltip";
import LabelChip from "../../../../../components/LabelChip";
import LabelChipWithDropDown from "../../components/LabelsDropDown/LabelChipWithDropDown";
import { useEntities } from "../../hooks/useEntities";
import MoreLabelsChip from "./MoreLabelsChip";

const LabelsCell = ({ entity }) => {
  const cellRef = useRef(null);
  const hiddenRef = useRef(null);
  const [cellWidth, setCellWidth] = useState(0);
  const [widthsOfChips, setWidthsOfChips] = useState([]);
  const { getAllLabels } = useAPI();
  const { useQueryForEntitiesByType } = useEntities();

  const entitiesQuery = useQueryForEntitiesByType({
    type: entity?.entity_type,
    enabled: Boolean(entity?.entity_type),
  });

  const entities = entitiesQuery.data || [];
  const cachedEntity = Array.isArray(entities)
    ? entities.find((e) => e.entity_id === entity?.entity_id)
    : null;
  const currentEntity = cachedEntity || entity;

  const GAPS = 8;
  const MORE_LABELS_CHIP_WIDTH = 45;

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === cellRef.current) {
          const newWidth = entry.contentRect.width;
          setCellWidth(newWidth);
        }
      });
    });

    if (cellRef.current) {
      resizeObserver.observe(cellRef.current);
    }

    if (hiddenRef.current) {
      const widthsOfChips = Array.from(hiddenRef.current.children).map(
        (child) => child.offsetWidth
      );
      setWidthsOfChips(widthsOfChips);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [currentEntity?.labels]);

  const calculateLabelsToShowCount = () => {
    if (!currentEntity?.labels || currentEntity?.labels?.length === 0) return 0;
    if (currentEntity?.labels?.length === 1) return 1;

    let labelsToShowCount = currentEntity?.labels?.length;
    let totalWidth =
      GAPS * (labelsToShowCount - 1) +
      widthsOfChips.reduce((acc, width) => acc + width, 0);
    if (totalWidth <= cellWidth) return labelsToShowCount;

    // If not all are shown, add the "more labels" chip and remove the last label until it fits
    while (totalWidth > cellWidth) {
      labelsToShowCount--;
      totalWidth =
        GAPS * labelsToShowCount +
        widthsOfChips
          .slice(0, labelsToShowCount)
          .reduce((acc, width) => acc + width, 0) +
        MORE_LABELS_CHIP_WIDTH;
      if (totalWidth <= cellWidth) {
        return labelsToShowCount;
      }
      if (labelsToShowCount === 1) {
        return 1;
      }
    }
  };

  const labelsToShowCount = calculateLabelsToShowCount();

  const { data: allLabels } = useQuery({
    queryKey: labelKeys.all,
    queryFn: getAllLabels,
    select: (data) => data.data,
  });

  const labelIdToNameAndColorMap = {};
  allLabels
    ?.filter((label) => !label.is_deleted)
    ?.forEach((label) => {
      labelIdToNameAndColorMap[label.label_id] = {
        name: label.name,
        color: label.color,
      };
    });

  const validLabels =
    currentEntity?.labels
      ?.map((labelId) => labelIdToNameAndColorMap[labelId])
      ?.filter((labelNameAndColor) => labelNameAndColor?.name !== undefined) ||
    [];

  if (!validLabels || validLabels?.length === 0) return null;

  return (
    <>
      {/* Hidden Box with all labels chips present for width calculation */}
      <Box
        ref={hiddenRef}
        display="flex"
        position="absolute"
        visibility="hidden"
        height={0}
      >
        {validLabels?.map((label) => (
          <LabelChip
            key={label.name}
            text={label.name}
            type="outline"
            color={label.color}
          />
        ))}
      </Box>
      <Box
        ref={cellRef}
        width="100%"
        display="flex"
        alignItems="center"
        maxWidth="100%"
        gap={`${GAPS}px`}
        height="36px"
        padding="6px 8px 6px 8px"
      >
        {validLabels?.slice(0, labelsToShowCount)?.map((label) => (
          <LabelChipWithDropDown
            key={label.name}
            label={label.name}
            color={label.color}
            entities={[currentEntity]}
          />
        ))}
        {labelsToShowCount < validLabels?.length && (
          <CustomToolTip
            title={validLabels
              ?.slice(labelsToShowCount)
              ?.map((label) => label.name)
              .join(", ")}
            placement="top"
          >
            <Box>
              <MoreLabelsChip
                entity={currentEntity}
                labels={validLabels
                  ?.slice(labelsToShowCount)
                  ?.map((label) => label.name)}
                width={MORE_LABELS_CHIP_WIDTH}
              />
            </Box>
          </CustomToolTip>
        )}
      </Box>
    </>
  );
};

export default LabelsCell;
